import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, ShoppingBag, User, Heart, MessageCircle, Menu, X, LogOut, LayoutDashboard, Shield, Plus, Coins, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import NotificationBell from './NotificationBell';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <img src="/logo.png" alt="Venda de Garagem" className="h-10 w-auto" />
          </Link>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden md:flex" data-testid="search-form">
            <div className="flex w-full items-center border-2 border-primary rounded-full px-4 py-1.5 bg-background focus-within:ring-2 focus-within:ring-primary/30 transition-shadow">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Buscar produtos, serviços..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none text-sm px-3 placeholder:text-muted-foreground"
                data-testid="search-input"
              />
              <Button type="submit" size="sm" className="rounded-full h-8 px-4 text-xs font-semibold" data-testid="search-submit-btn">
                Buscar
              </Button>
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link to="/create-listing" data-testid="create-listing-btn">
                  <Button size="sm" className="gap-1.5 rounded-full hidden sm:flex">
                    <Plus className="h-4 w-4" /> Anunciar
                  </Button>
                </Link>

                {/* Saldo + Adicionar Saldo */}
                <Link to="/credits" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors" data-testid="header-credits-link">
                  <Coins className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-bold text-amber-700" data-testid="header-credit-balance">R$ {(user.credits || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  <Plus className="h-3 w-3 text-amber-500" />
                </Link>

                <NotificationBell />

                <Link to="/chat" className="relative p-2 hover:bg-muted rounded-full transition-colors" data-testid="chat-link">
                  <MessageCircle className="h-5 w-5 text-muted-foreground" />
                </Link>
                <Link to="/dashboard?tab=favorites" className="p-2 hover:bg-muted rounded-full transition-colors hidden sm:flex" data-testid="favorites-link">
                  <Heart className="h-5 w-5 text-muted-foreground" />
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 p-1.5 hover:bg-muted rounded-full transition-colors" data-testid="user-menu-trigger">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Coins className="h-3 w-3 text-primary" />
                        <span className="text-xs font-semibold text-primary">R$ {(user.credits || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                      <User className="h-4 w-4 mr-2" /> Meu Perfil
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/credits')} data-testid="menu-credits">
                      <Coins className="h-4 w-4 mr-2" /> Adicionar Saldo
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Shield className="h-4 w-4 mr-2" /> Painel Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                      <LogOut className="h-4 w-4 mr-2" /> Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="login-btn">
                  <Button variant="ghost" size="sm" className="text-sm">Entrar</Button>
                </Link>
                <Link to="/register" data-testid="register-btn">
                  <Button size="sm" className="rounded-full text-sm">Cadastrar</Button>
                </Link>
              </div>
            )}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="md:hidden pb-3">
          <form onSubmit={handleSearch} className="flex items-center border-2 border-primary rounded-full px-3 py-1.5 bg-background">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-sm px-2"
              data-testid="mobile-search-input"
            />
            <Button type="submit" size="sm" className="rounded-full h-7 px-3 text-xs" data-testid="mobile-search-btn">Buscar</Button>
          </form>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border py-4 space-y-2">
            {user ? (
              <>
                <Link to="/create-listing" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 hover:bg-muted rounded-lg">
                  <Plus className="h-5 w-5" /> Anunciar
                </Link>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 hover:bg-muted rounded-lg">
                  <LayoutDashboard className="h-5 w-5" /> Dashboard
                </Link>
                <Link to="/credits" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 hover:bg-muted rounded-lg">
                  <Coins className="h-5 w-5" /> Créditos
                </Link>
              </>
            ) : null}
          </div>
        )}
      </div>
    </header>
  );
}
